<template lang="pug">
.modal-card(style="width: auto")  
  header.modal-card-head
    p.modal-card-title {{escalationLevel === 1 ? 'Erinnerung' : 'Mahnung'}}

  section.modal-card-body(v-if="settings")
    ValidationObserver(ref="form")
      .generic-wrapper
        .container
          .two-column
            .column(v-if="formData.dueDate")
              h2.title.is-6 E-Mail Anhang
              iframe(ref="pdf-preview").pdf-preview
            .column
              .form.w640(v-if="formData.dueDate")
                h2.title.is-6 E-Mail schreiben
                ValidationProvider(name="E-Mail Betreff" rules="required" v-slot="{ errors, valid }") 
                  b-field(label="E-Mail Betreff" label-position="on-border"
                          :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors")
                    b-input(v-model="formData.subject")
                ValidationProvider(name="E-Mail Text" rules="required" v-slot="{ errors, valid }") 
                  b-field(label="E-Mail Text" label-position="on-border"
                          :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors")
                    vue-editor(v-model="formData.body" :editorOptions="editorOptions")
              .spacer.sm
              .form.w640(v-if="settings.canOverridePaymentReminderDueDays")
                h2.title.is-6 Einstellungsmöglichkeiten
                b-message(v-if="!formData.dueDate" type="is-danger" has-icon) Bitte wählen Sie eine Mahnungsfälligkeit aus, um eine PDF-Vorschau zu sehen.
                v-date-picker(:value='formData.dueDate' @dayclick="d => updateDueDate(d)" :popover="popoverConfig" :min-date='new Date()')
                  template(v-slot='{ inputValue, inputEvents }')
                    b-field(:label="escalationLevel === 1 ? 'Erinnerungsfälligkeit' : 'Mahnungsfälligkeit'" label-position="on-border")
                      input.dt-selection-date(:value='formData.dueDate ? formatDate(formData.dueDate) : null' v-on='inputEvents')
                    .disclaimer(v-if="formData.dueDate") Der Gast hat somit <b>{{dueDateGuestTime === 1 ? dueDateGuestTime + ' Tag' : dueDateGuestTime + ' Tage'}}</b> Zeit, um auf die Mahnung zu reagieren.
                //- b-button(type="is-light" @click="generatePDF()") PDF & E-Mail aktualisieren

      
  footer.modal-card-foot
    button.button.is-light(@click="$emit('close')") Abbrechen
    button.button.is-secondary(@click="sendPaymentReminder()") Speichern ohne Senden
    button.button.is-primary(@click="sendPaymentReminder('sendEmail')") Senden & Speichern
</template>

<script>
import { DateTime } from 'luxon';

export default {
  props: {
    booking: {
      type: Object
    },
    show: {
      type: Boolean,
      default: false
    },
    escalationLevel: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      settings: null,
      popoverConfig: {
        visibility: 'focus'
      },
      formData: {
        subject: '',
        body: null,
        dueDate: null,
        reminderFee: 0
      },
      editorOptions: {
        placeholder: "Bitte füllen Sie dieses Feld aus ...",
        theme: "snow",
        modules: {
          toolbar: [
            ["bold", "italic", "underline", "strike", "link"], // toggled buttons
            ["blockquote"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ script: "sub" }, { script: "super" }], // superscript/subscript
            [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
            [{ color: [] }, { background: [] }], // dropdown with defaults from theme
            [{ align: [] }],

            ["clean"] // remove formatting button
          ]
        }
      }
    };
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    this.fullBooking = await this.$store.dispatch("rentObjectBookings/findOne", {id: this.booking.id});
    this.settings = await this.$store.dispatch("settings/find");

    if (this.escalationLevel === 1) { // PaymentReminder1
      const dueDayWithPotentialWeekend = DateTime.local().plus({days: this.settings.paymentReminder1DueDays});
      if ([6, 7].includes(dueDayWithPotentialWeekend.weekday)) {
        this.formData.dueDate = dueDayWithPotentialWeekend.plus({days: 2}).set({ weekday: 1, hour: 23, minute: 59 }).toISO();
      } else {
        this.formData.dueDate = dueDayWithPotentialWeekend.set({ hour: 23, minute: 59 }).toISO();
      }
    } else { // PaymentReminder2
      const dueDayWithPotentialWeekend = DateTime.local().plus({days: this.settings.paymentReminder2DueDays});
      if ([6, 7].includes(dueDayWithPotentialWeekend.weekday)) {
        this.formData.dueDate = dueDayWithPotentialWeekend.plus({days: 2}).set({ weekday: 1, hour: 23, minute: 59 }).toISO();
      } else {
        this.formData.dueDate = dueDayWithPotentialWeekend.set({ hour: 23, minute: 59 }).toISO();
      }
    }
      
    this.generateEmail();
    this.generatePDF();
    loadingComponent.close();
  },
  computed: {
    dueDateGuestTime() {
      if (!this.formData.dueDate) return;
      return Math.floor(DateTime.fromISO(this.formData.dueDate).diff(DateTime.local(), 'days').toObject().days);
    }
  },
  methods: {
    formatDate(d) {
      const dt = DateTime.fromISO(d);
      return dt.toLocaleString({
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
      });
    },
    updateDueDate(newVal) {
      const dueDate = DateTime.fromJSDate(newVal.date).set({hour: 23, minute: 59});
      this.formData.dueDate = dueDate.toISO();
      this.generateEmail();
      this.generatePDF();
    },
    async generateEmail() {
      const emailTemplates = await this.$store.dispatch("emailTemplates/findAll");
      let reminderTemplate = emailTemplates.find(e => e.uuid === `PAYMENT_REMINDER_${this.escalationLevel}_EMAIL`); // based on escalation level we show the correct template
      const result = await this.$store.dispatch("emailTemplates/replaceTemplateValues", {template: reminderTemplate, booking: {...this.booking, ...this.fullBooking, ...this.formData}});
      this.formData.subject = result.template.subject;
      this.formData.body = result.template.body;
    },
    async generatePDF() {
      const loadingComponent = this.$buefy.loading.open();
      
      // Here as well we show the preview of the respective escalation level
      const pdfArraybuffer = await this.$store.dispatch(`rentObjectBookings/paymentReminder${this.escalationLevel}PDFPreview`, {id: this.fullBooking.id, ...this.formData, ...this.data});
      const pdfBlob = new Blob([pdfArraybuffer], { type: "application/pdf" });
      this.$refs["pdf-preview"].src = URL.createObjectURL(pdfBlob);

      loadingComponent.close();
    },
    sendPaymentReminder(sendEmail) {
      this.$refs.form.validate().then(async success => {
        if (!success) {
          return;
        }

        const loadingComponent = this.$buefy.loading.open();
        await this.$store.dispatch("paymentReminders/create", {rentObjectBookingId: this.booking.id, ...this.formData, ...this.data, sendEmail, escalationLevel: this.escalationLevel});
        loadingComponent.close();
        
        this.$emit('refreshInbox');
        this.$emit('close');

        // Wait until the models are updated in the UI
        this.$nextTick(() => {
          this.$refs.form.reset();
        });
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.two-column {
  display: flex;
  justify-content: space-between;
}

.pdf-preview {
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 280px);
}
</style>